import React, { FC, ReactNode, useCallback } from "react";
import { ActionsGroup, Dialog, DialogActions, DialogTitle, FlatButton, IconButton, RaisedButton } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { DialogContent } from "./styled";

import { useDialogInfo } from "../../../hooks/useDialogs";

export type ConfirmDialogOptions = {
  title?: string;
  content: ReactNode;
  okText?: string;
  cancelText?: string;
  onConfirm?: VoidFunction;
  onCancel?: VoidFunction;
  options?: {
    maxWidth: string;
    minHeight: string;
    closable?: boolean;
  };
};

export const CONFIRM_DIALOG_DEFAULTS = {
  maxWidth: "27.6rem",
  minHeight: "0",
};

export const Confirmation: FC = () => {
  const { t } = useTranslation("common");
  const { options: dialogOptions, closeDialog } = useDialogInfo<string[], ConfirmDialogOptions>(true);
  const { title, content, okText, cancelText, onConfirm, onCancel, options } = dialogOptions;

  const handleConfirm = useCallback(() => {
    typeof onConfirm === "function" && onConfirm();
    closeDialog();
  }, [closeDialog, onConfirm]);

  const handleCancel = useCallback(() => {
    typeof onCancel === "function" && onCancel();
    closeDialog();
  }, [closeDialog, onCancel]);

  return (
    <Dialog
      isOpen
      onCloseRequest={handleCancel}
      modal
      maxWidth={options && options.maxWidth ? options.maxWidth : CONFIRM_DIALOG_DEFAULTS.maxWidth}
      minHeight={options && options.minHeight ? options.minHeight : CONFIRM_DIALOG_DEFAULTS.minHeight}
    >
      {title && (
        <DialogTitle>
          {!options?.closable && title}
          {options?.closable && (
            <>
              <ActionsGroup>{title}</ActionsGroup>
              <ActionsGroup>
                <IconButton kind="close" onClick={handleCancel} />
              </ActionsGroup>
            </>
          )}
        </DialogTitle>
      )}
      {!title && options?.closable && (
        <DialogTitle>
          <ActionsGroup />
          <ActionsGroup>
            <IconButton kind="close" onClick={handleCancel} />
          </ActionsGroup>
        </DialogTitle>
      )}
      <DialogContent
        style={{
          paddingTop: title ? "1rem" : !title && options?.closable ? 0 : "3.5rem",
        }}
      >
        {content}
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <RaisedButton primary onClick={handleConfirm}>
            {okText || t("actions.ok")}
          </RaisedButton>
          <FlatButton onClick={handleCancel}>{cancelText || t("actions.cancel")}</FlatButton>
        </ActionsGroup>
      </DialogActions>
    </Dialog>
  );
};
